import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import {
  ALIGNMENT,
  RATIO,
  TypographySize,
  SEMANTIC_TAG,
  ALIGNMENT_OPTIONS,
} from 'mm-theme-configuration/dist/consts';
import { CardOverlay } from '../../CardOverlay';
import { Image } from '../../image/Image';
import { useTheme } from '../../../../theming/useTheme';
import { PlayIcon } from '../../PlayIcon';
import { WrapperForCard } from '../WrapperForCard';
import { Link } from '../../link/Link';
import { semanticCSS } from '../../../../typography/semanticTags';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';
import {
  CardComponentDataProps,
  CardComponentPropsFromLayout,
  listAuthors,
  showAuthorInAnyScreenSize,
  showDateInAnyScreenSize,
  showMetadataInAnyScreenSize,
  showMetadataInLargeScreenSize, showMetadataInMediumScreenSize, showMetadataInSmallScreenSize,
  showPipeInAnyScreenSize,
} from '../cards.utils';
import { DynamicSemanticTag } from '../../../../typography/semanticTags/DynamicSemanticTag';
import { createImageAspectRatios } from '../../image/image.utils';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { baseUnit } from '../../../../theming/baseUnitDefinition';
import { ellipsify } from '../../../../typography/ellipsify';

export const componentConfigThemeKey = 'onTopCard';

export interface OnTopCardComponentThemeProps {
  cardTitleColorTypeNormal: string;
  cardTitleColorTypeHover: string;
  cardTitleColorTypeActive: string;
  borderRadius: number;
  boxShadowTypeNormal: string;
  boxShadowTypeActive: string;
  boxShadowTypeHover: string;
  borderWidthTypeNormal: number;
  borderWidthTypeHover: number;
  borderWidthTypeActive: number;
  borderColorTypeNormal: string;
  borderColorTypeHover: string;
  borderColorTypeActive: string;
  aspectRatioLarge: RATIO;
  aspectRatioMedium: RATIO;
  aspectRatioSmall: RATIO;
  overlayColor: string;
  titleFontSizeLarge: TypographySize;
  titleFontSizeMedium: TypographySize;
  titleFontSizeSmall: TypographySize;
  titleAlignment: ALIGNMENT;
  metadataAlignment: ALIGNMENT;
  pipeColor: string;
  showAuthorInSmallScreen: boolean;
  showAuthorInMediumScreen: boolean;
  showAuthorInLargeScreen: boolean;
  showDateInSmallScreen: boolean;
  showDateInMediumScreen: boolean;
  showDateInLargeScreen: boolean;
  metadataFontSizeLarge: TypographySize;
  metadataFontSizeMedium: TypographySize;
  metadataFontSizeSmall: TypographySize;
  authorColor: string;
  dateColor: string;
  wrapperGapLarge: number;
  wrapperGapMedium: number;
  wrapperGapSmall: number;
  verticalGapLarge: number;
  verticalGapMedium: number;
  verticalGapSmall: number;
  horizontalGapLarge: number;
  horizontalGapMedium: number;
  horizontalGapSmall: number;
  titleSemanticTag: SEMANTIC_TAG;
  metadataSemanticTag: SEMANTIC_TAG;
  showMainCategoryInLargeScreen: boolean;
  showMainCategoryInMediumScreen: boolean;
  showMainCategoryInSmallScreen: boolean;
  mainCategorySemanticTag: SEMANTIC_TAG;
  mainCategoryFontSizeLarge: TypographySize;
  mainCategoryFontSizeMedium: TypographySize;
  mainCategoryFontSizeSmall: TypographySize;
  mainCategoryTextColor: string;
  mainCategoryBackgroundColor: string;
  mainCategoryVerticalGapLarge: number;
  mainCategoryVerticalGapMedium: number;
  mainCategoryVerticalGapSmall: number;
  mainCategoryHorizontalGapLarge: number;
  mainCategoryHorizontalGapMedium: number;
  mainCategoryHorizontalGapSmall: number;
  mainCategoryAlignment: ALIGNMENT;
  numberOfLinesForTitle: number;
  numberOfLinesForAuthors: number;
}

type OnTopCardProps = CardComponentDataProps & CardComponentPropsFromLayout

const createCardStyles = (cardTextColorTypeNormal: string, cardTextColorTypeHover: string, cardTextColorTypeActive: string, borderRadius: number) => StyleSheet.create({
  card: {
    width: '100%',
    height: '100%',
    position: 'relative',
    textDecoration: 'none',
    color: cardTextColorTypeNormal,
    ':hover': {
      color: cardTextColorTypeHover,
    },
    ':active': {
      color: cardTextColorTypeActive,
    },
    display: 'block',
    '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
    borderRadius: `${borderRadius}px`,
  },
});

const createMetadataStyle = (horizontalGapLarge: number, horizontalGapMedium: number, horizontalGapSmall: number,
  metadataAlignment: ALIGNMENT, showMetadataInLargeScreen: boolean, showMetadataInMediumScreen: boolean,
  showMetadataInSmallScreen: boolean) => StyleSheet.create({
  style: {
    gridTemplateColumns: 'auto auto max-content',
    justifySelf: metadataAlignment === ALIGNMENT_OPTIONS.CENTER ? 'center' : 'left',
    [MEDIA_BREAKPOINTS.large]: {
      display: showMetadataInLargeScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapLarge}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMetadataInMediumScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapMedium}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMetadataInSmallScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapSmall}px`,
    },
  },
}).style;

const createTitleAlignmentStyle = (titleAlignment: ALIGNMENT) => {
  switch (titleAlignment) {
    case ALIGNMENT_OPTIONS.CENTER:
      return StyleSheet.create({
        style: {
          textAlign: 'center',
        },
      }).style;
    case ALIGNMENT_OPTIONS.START:
    default:
      return StyleSheet.create({
        style: {
          textAlign: 'left',
        },
      }).style;
  }
};

const createAuthorsStyle = (showAuthorSmallScreen: boolean, showAuthorMediumScreen: boolean,
  showAuthorLargeScreen: boolean, authorColor: string, numberOfLinesForAuthors: number) => StyleSheet.create({
  style: {
    color: authorColor,
    height: 'fit-content',
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...showAuthorLargeScreen ? ellipsify(numberOfLinesForAuthors).style : { display: 'none' },
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...showAuthorMediumScreen ? ellipsify(numberOfLinesForAuthors).style : { display: 'none' },
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...showAuthorSmallScreen ? ellipsify(numberOfLinesForAuthors).style : { display: 'none' },
    },
  },
}).style;

const createDateStyle = (showDateSmallScreen: boolean, showDateMediumScreen: boolean,
  showDateLargeScreen: boolean, dateColor: string) => StyleSheet.create({
  style: {
    color: dateColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateLargeScreen ? 'inline-block' : 'none',
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateMediumScreen ? 'inline-block' : 'none',
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateSmallScreen ? 'inline-block' : 'none',
    },
  },
}).style;

const createPipeStyle = (showDateSmallScreen: boolean, showDateMediumScreen: boolean, showDateLargeScreen: boolean,
  showAuthorSmallScreen: boolean, showAuthorMediumScreen: boolean, showAuthorLargeScreen: boolean, pipeColor: string) => StyleSheet.create({
  style: {
    color: pipeColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateLargeScreen && showAuthorLargeScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateMediumScreen && showAuthorMediumScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateSmallScreen && showAuthorSmallScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`,
    },
  },
}).style;

const createTextWrapperStyle = (horizontalGapLarge: number, horizontalGapMedium: number, horizontalGapSmall: number, verticalGapLarge: number, verticalGapMedium: number, verticalGapSmall: number) => StyleSheet.create({
  style: {
    overflow: 'hidden',
    width: '-webkit-fill-available',
    display: 'grid',
    background: 'transparent',
    position: 'absolute',
    bottom: 0,
    [MEDIA_BREAKPOINTS.large]: {
      margin: `${baseUnit * horizontalGapLarge}px ${baseUnit * verticalGapLarge}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit * horizontalGapMedium}px ${baseUnit * verticalGapMedium}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit * horizontalGapSmall}px ${baseUnit * verticalGapSmall}px`,
    },
  },
}).style;

const createMainCategoryStyle = (
  showMainCategoryInLargeScreen: boolean,
  showMainCategoryInMediumScreen: boolean,
  showMainCategoryInSmallScreen: boolean,
  mainCategoryHorizontalGapLarge: number,
  mainCategoryHorizontalGapMedium: number,
  mainCategoryHorizontalGapSmall: number,
  mainCategoryVerticalGapLarge: number,
  mainCategoryVerticalGapMedium: number,
  mainCategoryVerticalGapSmall: number,
  mainCategoryBackgroundColor: string,
  mainCategoryTextColor: string,
  mainCategoryAlignment: ALIGNMENT,
  horizontalGapLarge: number,
  horizontalGapMedium: number,
  horizontalGapSmall: number,
) => StyleSheet.create({
  style: {
    backgroundColor: mainCategoryBackgroundColor,
    color: mainCategoryTextColor,
    width: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: {
      display: showMainCategoryInLargeScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapLarge * baseUnit}px ${mainCategoryVerticalGapLarge * baseUnit}px`,
      margin: mainCategoryAlignment === ALIGNMENT_OPTIONS.CENTER ? `${horizontalGapLarge * baseUnit}px auto` : `${horizontalGapLarge * baseUnit}px 0`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMainCategoryInMediumScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapMedium * baseUnit}px ${mainCategoryVerticalGapMedium * baseUnit}px`,
      margin: mainCategoryAlignment === ALIGNMENT_OPTIONS.CENTER ? `${horizontalGapMedium * baseUnit}px auto` : `${horizontalGapMedium * baseUnit}px 0`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMainCategoryInSmallScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapSmall * baseUnit}px ${mainCategoryVerticalGapSmall * baseUnit}px`,
      margin: mainCategoryAlignment === ALIGNMENT_OPTIONS.CENTER ? `${horizontalGapSmall * baseUnit}px auto` : `${horizontalGapSmall * baseUnit}px 0`,
    },
  },
}).style;

export const OnTopCard: React.FunctionComponent<OnTopCardProps> = props => {
  const {
    title,
    image,
    articleUrl,
    coverType,
    imageHeightCalculationMethods,
    imageWidths,
    lazyLoad = false,
    authors,
    createdAt,
    mainCategory,
  } = props;
  const {
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    boxShadowTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover,
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    showAuthorInSmallScreen,
    showAuthorInMediumScreen,
    showAuthorInLargeScreen,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    authorColor,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    metadataAlignment,
    titleAlignment,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    overlayColor,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    metadataSemanticTag,
    showDateInSmallScreen,
    showDateInMediumScreen,
    showDateInLargeScreen,
    dateColor,
    pipeColor,
    showMainCategoryInLargeScreen,
    showMainCategoryInMediumScreen,
    showMainCategoryInSmallScreen,
    mainCategoryHorizontalGapLarge,
    mainCategoryHorizontalGapMedium,
    mainCategoryHorizontalGapSmall,
    mainCategoryVerticalGapLarge,
    mainCategoryVerticalGapMedium,
    mainCategoryVerticalGapSmall,
    mainCategoryBackgroundColor,
    mainCategoryFontSizeLarge,
    mainCategoryFontSizeMedium,
    mainCategoryFontSizeSmall,
    mainCategorySemanticTag,
    mainCategoryTextColor,
    mainCategoryAlignment,
    numberOfLinesForTitle,
    numberOfLinesForAuthors,
  } = useTheme<OnTopCardComponentThemeProps>(componentConfigThemeKey);
  const cardStyles = createCardStyles(cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, borderRadius);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
  };
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, borderBoxShadow });
  const titleAlignmentStyle = createTitleAlignmentStyle(titleAlignment);
  const showMetadataInLargeScreen = showMetadataInLargeScreenSize(showAuthorInLargeScreen, showDateInLargeScreen);
  const showMetadataInMediumScreen = showMetadataInMediumScreenSize(showAuthorInMediumScreen, showDateInMediumScreen);
  const showMetadataInSmallScreen = showMetadataInSmallScreenSize(showAuthorInSmallScreen, showDateInSmallScreen);
  const metadataStyle = createMetadataStyle(horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, metadataAlignment, showMetadataInLargeScreen, showMetadataInMediumScreen, showMetadataInSmallScreen);
  const authorsStyle = createAuthorsStyle(showAuthorInSmallScreen, showAuthorInMediumScreen, showAuthorInLargeScreen, authorColor, numberOfLinesForAuthors);
  const dateStyle = createDateStyle(showDateInSmallScreen, showDateInMediumScreen, showDateInLargeScreen, dateColor);
  const pipeStyle = createPipeStyle(showDateInSmallScreen, showDateInMediumScreen, showDateInLargeScreen, showAuthorInSmallScreen, showAuthorInMediumScreen, showAuthorInLargeScreen, pipeColor);
  const showAuthor = showAuthorInAnyScreenSize(authors, showAuthorInLargeScreen, showAuthorInMediumScreen, showAuthorInSmallScreen);
  const showDate = showDateInAnyScreenSize(showDateInLargeScreen, showDateInMediumScreen, showDateInSmallScreen);
  const showMainCategory = showMainCategoryInSmallScreen || showMainCategoryInMediumScreen || showMainCategoryInLargeScreen;
  // @ts-ignore TS2345
  const { style } = StyleSheet.create(ellipsify(numberOfLinesForTitle));
  const textWrapperStyle = createTextWrapperStyle(horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, verticalGapLarge, verticalGapMedium, verticalGapSmall);

  const MainCategory: React.FunctionComponent = () => {
    const mainCategoryStyle = createMainCategoryStyle(
      showMainCategoryInLargeScreen,
      showMainCategoryInMediumScreen,
      showMainCategoryInSmallScreen,
      mainCategoryHorizontalGapLarge,
      mainCategoryHorizontalGapMedium,
      mainCategoryHorizontalGapSmall,
      mainCategoryVerticalGapLarge,
      mainCategoryVerticalGapMedium,
      mainCategoryVerticalGapSmall,
      mainCategoryBackgroundColor,
      mainCategoryTextColor,
      mainCategoryAlignment,
      horizontalGapLarge,
      horizontalGapMedium,
      horizontalGapSmall,
    );
    return (
      <DynamicSemanticTag
        semanticTag={mainCategorySemanticTag}
        fontSizeLarge={mainCategoryFontSizeLarge}
        fontSizeMedium={mainCategoryFontSizeMedium}
        fontSizeSmall={mainCategoryFontSizeSmall}
        styles={semanticCSS(mainCategoryStyle)}
      >
        {mainCategory}
      </DynamicSemanticTag>
    );
  };

  return (
    <WrapperForCard
      boxShadowStyle={boxShadowStyle}
      borderRadius={borderRadius}
      paddingLarge={wrapperGapLarge}
      paddingMedium={wrapperGapMedium}
      paddingSmall={wrapperGapSmall}
    >
      <Link className={css(cardStyles.card, boxShadowStyle.style)} href={articleUrl}>
        <Image {...image} lazyLoad={lazyLoad} imageAspectRatios={createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge)} imageHeightCalculationMethods={imageHeightCalculationMethods} imageWidths={imageWidths} />
        <CardOverlay overlayColor={overlayColor} />
        <div className={css(textWrapperStyle)}>
          <header>
            {showMainCategory ? <MainCategory /> : null}
            <DynamicSemanticTag
              semanticTag={titleSemanticTag}
              fontSizeLarge={titleFontSizeLarge}
              fontSizeMedium={titleFontSizeMedium}
              fontSizeSmall={titleFontSizeSmall}
              styles={semanticCSS(titleAlignmentStyle, style)}
            >
              {title}
            </DynamicSemanticTag>
          </header>
          { showMetadataInAnyScreenSize(showAuthor, showDate) ? (
            <footer>
              <DynamicSemanticTag
                semanticTag={metadataSemanticTag}
                fontSizeLarge={metadataFontSizeLarge}
                fontSizeMedium={metadataFontSizeMedium}
                fontSizeSmall={metadataFontSizeSmall}
                styles={semanticCSS(metadataStyle)}
              >
                { showAuthor ? (<div className={css(authorsStyle)}>{listAuthors(authors)}</div>) : null }
                { showPipeInAnyScreenSize(showAuthor, showDate) ? (<div className={css(pipeStyle)}>|</div>) : null }
                { showDate ? (<div className={css(dateStyle)}>{createdAt}</div>) : null }
              </DynamicSemanticTag>
            </footer>
          ) : null }
        </div>
        {coverType === 'video' ? <PlayIcon /> : null}
      </Link>
    </WrapperForCard>
  );
};
